import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BRAND_SANS_FONT } from '../design/theme';

// =============================================================================
// ComparisonSlide
// -----------------------------------------------------------------------------
// A STATIC (no animation, no app-window chrome, no agent sidebar, no workflow
// steps) 16:9 "deliverable" used on the /vs/claude comparison page. Both
// variants render the SAME slide TOPIC (a Nordic payments market-sizing slide)
// but at two clearly different levels of rigour for two different audiences:
//
//   'generalised'  (a competent generic draft): a soft takeaway title, bold-lead
//                  bullets with light unsourced numbers, one grey chart, and a
//                  vague takeaway strip. Credibly decent - but no citations,
//                  methodology, TAM/SAM/SOM, or sources (that rigour is the
//                  specialised side).
//
//   'specialised'  (Restack, for consulting / PE deal teams): a sharp action
//                  title, TAM / SAM / SOM metrics with citation superscripts, a
//                  methodology footnote, and numbered sources. Visibly denser,
//                  board-ready, and more rigorous.
//
// The slide content is authored once at a fixed DESIGN size (DESIGN_W x DESIGN_H,
// a true 16:9), then scaled to fit its column with a container query, so it
// stays exactly 16:9 and stays legible at any width (desktop or the narrow
// stacked mobile column) without reflow or clipping. Same idea as the office
// window mockups: design once, scale the whole thing down.
//
// Design system: ACCENT #B83B1D, INK #2E140A, BRAND_SANS_FONT, MUI sx.
// No em dashes in copy.
// =============================================================================

const ACCENT = '#B83B1D';
const ACCENT2 = '#D4694E';
const INK = '#2E140A';
const WHITE = '#FFFFFF';

// Fixed slide design size (16:9). Everything inside is authored at this size and
// scaled uniformly to the actual column width via transform.
const DESIGN_W = 560;
const DESIGN_H = (DESIGN_W * 9) / 16; // 315

const tint = (a) => alpha(ACCENT, a);
const inkTint = (a) => alpha(INK, a);

// --- the bar chart (shared by both variants, drawn at two fidelities) --------
// `cited` adds the superscript axis numbers + accent highlight bar that signal
// the specialised, sourced read.
const Chart = ({ data, accent, cited, height }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.7, height }}>
    {data.map((d, i) => {
      const isAccent = cited ? i === data.length - 1 : false;
      return (
        <Box
          key={d.y}
          sx={{
            flex: 1,
            minWidth: 0,
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 0.4,
          }}
        >
          {d.v && (
            <Typography
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: '0.5rem',
                fontWeight: isAccent ? 700 : 600,
                lineHeight: 1,
                color: isAccent ? ACCENT : inkTint(0.55),
              }}
            >
              {d.v}
            </Typography>
          )}
          <Box
            sx={{
              width: '100%',
              height: `${d.h}%`,
              borderRadius: '3px 3px 0 0',
              background: isAccent
                ? `linear-gradient(180deg, ${ACCENT} 0%, ${ACCENT2} 100%)`
                : accent
                ? tint(0.22)
                : inkTint(0.16),
              boxShadow: isAccent ? `0 2px 8px ${tint(0.2)}` : 'none',
            }}
          />
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.5rem',
              fontWeight: isAccent ? 700 : 450,
              lineHeight: 1,
              color: isAccent ? ACCENT : inkTint(0.4),
            }}
          >
            {d.y}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

// =============================================================================
// GENERALISED (a competent generic draft). A soft takeaway title, bold-lead
// bullets with light unsourced numbers, one grey chart, and a vague takeaway
// strip. Credibly decent - visibly missing the citations, methodology,
// TAM/SAM/SOM, and sources of the specialised side.
// =============================================================================
const GeneralisedBody = () => (
  <>
    <Box>
      <Typography
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: '1.02rem',
          fontWeight: 700,
          letterSpacing: '-0.012em',
          color: INK,
          lineHeight: 1.18,
        }}
      >
        The Nordic payments market is large and growing
      </Typography>
      <Typography
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: '0.62rem',
          fontWeight: 500,
          color: inkTint(0.45),
          mt: 0.3,
        }}
      >
        Market overview | 2026
      </Typography>
    </Box>

    {/* unsourced stat tiles: the generic cousin of the specialised side's
        cited TAM / SAM / SOM metrics */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 1 }}>
      {[
        { v: '$2B+', l: 'market today' },
        { v: '2x', l: 'growth by 2029' },
        { v: '25M', l: 'digital users' },
      ].map((s) => (
        <Box key={s.l} sx={{ borderRadius: '6px', background: inkTint(0.035), px: 0.9, py: 0.5 }}>
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.78rem',
              fontWeight: 800,
              letterSpacing: '-0.01em',
              color: inkTint(0.8),
              lineHeight: 1.15,
            }}
          >
            {s.v}
          </Typography>
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.48rem',
              fontWeight: 500,
              color: inkTint(0.5),
              lineHeight: 1.2,
            }}
          >
            {s.l}
          </Typography>
        </Box>
      ))}
    </Box>

    {/* Bold-lead bullets with light, unsourced quantification - decent, but
        no citations, methodology, or sources. */}
    <Box sx={{ display: 'flex', gap: 2.6, flex: 1, minHeight: 0, mt: 1, alignItems: 'center' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
        {[
          { lead: 'A large market', rest: ' - worth around $2B across the Nordics today' },
          { lead: 'Strong growth', rest: ' - the market could double by 2029' },
          { lead: 'Room for new entrants', rest: ' - especially in B2B payments' },
        ].map((b) => (
          <Box key={b.lead} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.9 }}>
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: inkTint(0.35),
                flexShrink: 0,
                mt: '0.5em',
              }}
            />
            <Typography
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: '0.76rem',
                fontWeight: 450,
                color: inkTint(0.7),
                lineHeight: 1.35,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700, color: inkTint(0.85) }}>{b.lead}</Box>
              {b.rest}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* a labelled grey chart: no CAGR badge, no source superscript, no
          highlight bar */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.55 }}>
        <Typography
          sx={{
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.62rem',
            fontWeight: 600,
            color: inkTint(0.55),
          }}
        >
          Market size ($B)
        </Typography>
        <Chart
          data={[
            { h: 50, v: '$2.1', y: '2026' },
            { h: 67, v: '$2.8', y: '2027' },
            { h: 83, v: '$3.5', y: '2028' },
            { h: 100, v: '$4.2', y: '2029' },
          ]}
          height={56}
        />
      </Box>
    </Box>

    {/* a vague takeaway strip where the specialised side has methodology +
        numbered sources */}
    <Box
      sx={{
        mt: 0.9,
        px: 1.1,
        py: 0.55,
        borderRadius: '6px',
        background: inkTint(0.035),
      }}
    >
      <Typography
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: '0.56rem',
          fontWeight: 500,
          color: inkTint(0.55),
          lineHeight: 1.3,
        }}
      >
        <Box component="span" sx={{ fontWeight: 700 }}>Takeaway:</Box> the
        Nordics look like an attractive market for payments companies
      </Typography>
    </Box>
  </>
);

// =============================================================================
// SPECIALISED (Restack). Action title, TAM / SAM / SOM metrics with citation
// superscripts, a sourced revenue chart, a methodology footnote, and a numbered
// source bar. Dense, board-ready.
// =============================================================================
const METRICS = [
  { label: 'TAM', value: '$4.2B', cite: '1', desc: 'Nordic payments + digital banking' },
  { label: 'SAM', value: '$1.8B', cite: '2', desc: 'B2B SaaS-accessible segment' },
  { label: 'SOM', value: '$340M', cite: '3', desc: 'By 2029 at 3.2% penetration' },
];
const SOURCES = ['Nordic Capital Markets 2026', 'ECB Fintech Q3 2026', 'Dealroom Nordic', 'Statista Payments'];

const SpecialisedBody = () => (
  <>
    <Box>
      <Typography
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: '0.98rem',
          fontWeight: 800,
          letterSpacing: '-0.012em',
          color: INK,
          lineHeight: 1.18,
        }}
      >
        A $340M obtainable market by 2029, growing at 41% a year
      </Typography>
      <Typography
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: '0.62rem',
          fontWeight: 500,
          color: inkTint(0.45),
          mt: 0.3,
        }}
      >
        Payment Processing &amp; Digital Banking | Q4 2026 Market Entry Analysis
      </Typography>
    </Box>

    {/* TAM / SAM / SOM metrics with citation superscripts */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.6, mt: 1.2 }}>
      {METRICS.map((m) => (
        <Box key={m.label} sx={{ borderLeft: `2px solid ${tint(0.35)}`, pl: 1.1 }}>
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.58rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: ACCENT,
            }}
          >
            {m.label}
          </Typography>
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '1.02rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: INK,
              lineHeight: 1.05,
            }}
          >
            {m.value}
            <Box
              component="sup"
              sx={{ fontSize: '0.5em', fontWeight: 700, color: ACCENT, ml: '1px', verticalAlign: 'super' }}
            >
              {m.cite}
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.5rem',
              fontWeight: 450,
              color: inkTint(0.5),
              lineHeight: 1.25,
              mt: 0.2,
            }}
          >
            {m.desc}
          </Typography>
        </Box>
      ))}
    </Box>

    {/* sourced revenue chart */}
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography
          sx={{
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.58rem',
            fontWeight: 600,
            color: inkTint(0.55),
          }}
        >
          Revenue Projection ($M)
          <Box component="sup" sx={{ fontSize: '0.7em', fontWeight: 700, color: ACCENT, ml: '1px' }}>
            4
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.56rem',
            fontWeight: 700,
            color: ACCENT,
          }}
        >
          CAGR 41%
        </Typography>
      </Box>
      <Chart
        data={[
          { h: 35, v: '$120', y: '2026' },
          { h: 54, v: '$185', y: '2027' },
          { h: 76, v: '$260', y: '2028' },
          { h: 100, v: '$340', y: '2029' },
        ]}
        accent
        cited
        height={64}
      />
    </Box>

    {/* methodology footnote + numbered source bar */}
    <Box sx={{ mt: 0.8, pt: 0.6, borderTop: `1px solid ${inkTint(0.08)}` }}>
      <Typography
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: '0.46rem',
          fontStyle: 'italic',
          color: inkTint(0.42),
          lineHeight: 1.3,
          mb: 0.4,
        }}
      >
        Methodology: bottom-up SOM built from addressable merchant base x average take rate x
        modelled penetration; triangulated against top-down analyst TAM.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.15rem 1rem' }}>
        {SOURCES.map((s, i) => (
          <Box key={s} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: ACCENT,
                color: WHITE,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: BRAND_SANS_FONT,
                fontSize: '0.38rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {i + 1}
            </Box>
            <Typography
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: '0.46rem',
                fontWeight: 450,
                color: inkTint(0.5),
                whiteSpace: 'nowrap',
              }}
            >
              {s}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </>
);

// =============================================================================
// THE COMPONENT.
// -----------------------------------------------------------------------------
//   variant  'generalised' | 'specialised' (default 'specialised')
//   accent   optional accent colour for the top strip + page number (default ACCENT)
//   caption  optional small caption shown under the card (e.g. "Claude" / "Restack")
//   pageNum  optional page number in the corner (default 4)
//   sx       optional MUI sx forwarded to the outer wrapper
// =============================================================================
export default function ComparisonSlide({
  variant = 'specialised',
  accent = ACCENT,
  caption,
  pageNum = 4,
  sx,
}) {
  const isSpecialised = variant === 'specialised';
  const Body = isSpecialised ? SpecialisedBody : GeneralisedBody;

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {/* 16:9 frame: clips, rounds, and is the container the slide scales to. */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          background: WHITE,
          borderRadius: '14px',
          border: `1px solid ${inkTint(0.08)}`,
          boxShadow: `0 12px 32px ${inkTint(0.1)}, 0 0 0 1px ${inkTint(0.03)}`,
          overflow: 'hidden',
          containerType: 'inline-size',
        }}
      >
        {/* The slide is authored at the fixed DESIGN size and scaled uniformly
            to the frame width, so it reads identically (just smaller) at any
            column width and never overflows the 16:9 box. */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${DESIGN_W}px`,
            height: `${DESIGN_H}px`,
            transformOrigin: 'top left',
            transform: `scale(calc(100cqw / ${DESIGN_W}px))`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* thin top accent strip */}
          <Box
            sx={{
              height: 4,
              flexShrink: 0,
              background: `linear-gradient(90deg, ${accent} 0%, ${alpha(accent, 0.2)} 100%)`,
            }}
          />

          {/* slide body */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              p: 2.2,
            }}
          >
            <Body />
          </Box>

          {/* corner page number */}
          <Typography
            sx={{
              position: 'absolute',
              bottom: 9,
              right: 14,
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.56rem',
              fontWeight: 600,
              color: alpha(accent, 0.6),
              letterSpacing: '0.04em',
            }}
          >
            {pageNum}
          </Typography>
        </Box>
      </Box>

      {caption && (
        <Typography
          sx={{
            fontFamily: BRAND_SANS_FONT,
            fontSize: { xs: '0.62rem', md: '0.68rem' },
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: inkTint(0.5),
            textAlign: 'center',
            mt: 1.1,
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
}
